<template>
    <div class="main">
        <div class="body-content">
            <div class="page-header">
                <h1>Upload Booking No from Excel</h1>
            </div>
            <div class="form-group">
                <div class="form-item">
                    <label for="partyname">Party *</label>
                    <v-select :clearable="false" v-model="formData.partyName" :options="party"
                    label="short_name" :reduce="party => party.autoid" placeholder="Select Party Name"
                    tabindex="0" :selectOnTab="true" />
                </div>

                <div class="form-item">
                    <label for="movement">Movement *</label>
                    <select
                        id="movement"
                        v-model="formData.movement"
                        class="custom-select"
                    >
                        <option value="">Select Movement</option>
                        <option value="1">Import</option>
                        <option value="2">Export</option>
                    </select>
                </div>

                <div class="form-item">
                    <label for="excelFile">Excel File *</label>
                    <input
                        type="file"
                        id="excelFile"
                        @change="handleFileUpload"
                        class="custom-file-input"
                        accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                    />
                </div>
            </div>

            <div class="button-group">
                <button
                    type="button"
                    class="excel-button"
                    @click="uploadBookingNo"
                    :disabled="isSubmitting || !isFormValid()"
                >
                    <i class="fas fa-upload"></i> Upload
                </button>
                <button
                    type="button"
                    class="cancel-button"
                    @click="clearForm"
                    :disabled="isSubmitting"
                >
                    Clear
                </button>
            </div>

            <div v-if="message" class="message" :class="messageType">
                {{ message }}
            </div>

            <div v-if="uploadProgress" class="progress-container">
                <div class="progress-bar" :style="{ width: uploadProgress + '%' }"></div>
                <span class="progress-text">{{ uploadProgress }}%</span>
            </div>
            <div>
                <v-data-table :headers="tableHeaders" :items="tableData" :items-per-page="pageSize" v-model:page="currentPage"
        class="elevation-1"></v-data-table>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import VSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import { useToast } from 'vue-toastification';
import * as cs from '../assets/js/commonscript.js';

export default {
    name: 'UploadBookingNoForEntry',
    components: {
        VSelect,
    },
    data() {
        return {
            party: JSON.parse(localStorage.getItem('party')) || [],
            formData: {
                partyName: 0,
                applicantid: 0,
                accountid: 0,
                movement: '',
                file: null,
            },
            message: '',
            messageType: 'info',
            isSubmitting: false,
            uploadProgress: 0,
            tableHeaders: [
                { title: 'S.No', key: 'sno' },
                { title: 'VCN No', key: 'vcnno' },
                { title: 'Booking No', key: 'booking_no' },
                { title: 'Job Order No', key: 'job_order_no' },
                { title: 'Container No', key: 'container_no' },
                { title: 'Vessel Name', key: 'vesselname' },
                { title: 'Voyage Name', key: 'voyagename' },
                { title: 'Status', key: 'status' }
            ],
            tableData:[],
        };
    },
    methods: {
        updateApplicantAccountIds() {
            //console.log('Selected partyName:', this.formData.partyName);
            if (this.formData.partyName === 0) {
                this.formData.applicantid = 0;
                this.formData.accountid = 0;
                return;
            }

            const appAcc = cs.GetAppAccFromParty(this.formData.partyName);
            //console.log('Retrieved applicantid:', appAcc.applicantid, 'accountid:', appAcc.accountid);
            this.formData.applicantid = appAcc.applicantid;
            this.formData.accountid = appAcc.accountid;
        },
        handleFileUpload(event) {
            const file = event.target.files[0];
            if (!file) {
                this.formData.file = null;
                return;
            }

            const allowedTypes = [
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'application/vnd.ms-excel',
            ];
            const allowedExt = ['.xlsx', '.xls'];
            const name = file.name.toLowerCase();
            const isTypeAllowed = allowedTypes.includes(file.type);
            const isExtAllowed = allowedExt.some(ext => name.endsWith(ext));

            if (!isTypeAllowed && !isExtAllowed) {
                this.formData.file = null;
                event.target.value = '';
                useToast().error('Only Excel files (.xlsx, .xls) are allowed.');
                return;
            }

            this.formData.file = file;
        },
        isFormValid() {
            return (
                this.formData.partyName !== 0 &&
                this.formData.partyName !== '' &&
                this.formData.movement !== '' &&
                this.formData.file !== null
            );
        },
        async uploadBookingNo() {
            if (this.isSubmitting) return;

            if (!this.isFormValid()) {
                useToast().error('Please fill all required fields.');
                return;
            }
            this.updateApplicantAccountIds();
            this.isSubmitting = true;
            this.message = '';
            this.uploadProgress = 0;

            try {
                const token = localStorage.getItem('access_token');
                if (!token) {
                    useToast().error('Authentication token not found.');
                    this.isSubmitting = false;
                    return;
                }

                const config = {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                    maxContentLength: Infinity,
                    maxBodyLength: Infinity,
                    timeout: 15 * 60 * 1000, // 15 minutes timeout
                    onUploadProgress: (progressEvent) => {
                        this.uploadProgress = Math.round(
                            (progressEvent.loaded * 100) / progressEvent.total
                        );
                    },
                };

                this.message = 'Uploading file...';
                this.messageType = 'info';

                const payload = {
                    applicantid: this.formData.applicantid,
                    accountid: this.formData.accountid,
                    movement: this.formData.movement,
                    file: this.formData.file.name,
                };

                const data = new FormData();
                data.append('transaction', JSON.stringify(payload));
                data.append('file', this.formData.file);

                const response = await axios.post('/upload_bookingno_for_entry', data, config );
                //console.log(response)
                if (response.data && response.data[0] === 1) {
                    this.message = response.data[1] || 'File uploaded successfully.';
                    this.tableData = response.data[2];
                    this.messageType = 'success';
                    useToast().success('Booking numbers uploaded successfully.');
                    this.clearForm();
                } else {
                    const errorMsg = response.data[1] || 'Upload failed.';
                    this.message = errorMsg;
                    this.messageType = 'error';
                    useToast().error(errorMsg);
                }
            } catch (error) {
                console.error('Error uploading file:', error);
                const errorMessage =
                    error.response?.data?.detail ||
                    error.response?.data?.message ||
                    error.response?.data?.[1] ||
                    'Error uploading file.';
                this.message = errorMessage;
                this.messageType = 'error';
                useToast().error(errorMessage);
            } finally {
                this.isSubmitting = false;
                this.uploadProgress = 0;
            }
        },
        clearForm() {
            this.formData = {
                partyName: 0,
                applicantid: 0,
                accountid: 0,
                movement: '',
                file: null,
            };
            document.getElementById('excelFile').value = '';
            this.message = '';
            this.messageType = 'info';
            this.uploadProgress = 0;
        },
    },
};
</script>

<style scoped>

.form-group {
    display: flex;
    flex-wrap: wrap;
    gap: 15px;
    margin-bottom: 25px;
}

.form-item {
    flex: 1;
    min-width: 280px;
}

.form-item label {
    display: block;
    margin-bottom: 8px;
    font-weight: 600;
    color: #333;
}

select,
input[type="file"],
.vue-select {
    width: 100%;
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    background-color: #fff;
}

select:focus {
    outline: none;
    border-color: #17a2b8;
    box-shadow: 0 0 0 3px rgba(23, 162, 184, 0.1);
}

.custom-file-input {
    padding: 8px;
}

.button-group {
    display: flex;
    gap: 10px;
    margin-bottom: 20px;
    flex-wrap: wrap;
}

.excel-button,
.cancel-button {
    padding: 12px 24px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 1rem;
    font-weight: 600;
    transition: background-color 0.3s ease;
    display: flex;
    align-items: center;
    gap: 8px;
}

.excel-button {
    background-color: #17a2b8;
    color: white;
}

.excel-button:hover:not(:disabled) {
    background-color: #138496;
}

.excel-button:disabled {
    background-color: #6c757d;
    cursor: not-allowed;
    opacity: 0.6;
}

.cancel-button {
    background-color: #6c757d;
    color: white;
}

.cancel-button:hover:not(:disabled) {
    background-color: #5a6268;
}

.cancel-button:disabled {
    cursor: not-allowed;
    opacity: 0.6;
}

.message {
    padding: 12px 16px;
    border-radius: 4px;
    margin-top: 20px;
    margin-bottom: 20px;
    font-weight: 500;
}

.message.success {
    background-color: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
}

.message.error {
    background-color: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
}

.message.info {
    background-color: #d1ecf1;
    color: #0c5460;
    border: 1px solid #bee5eb;
}

.progress-container {
    margin-top: 20px;
    display: flex;
    align-items: center;
    gap: 10px;
}

.progress-bar {
    flex: 1;
    height: 20px;
    background-color: #e9ecef;
    border-radius: 4px;
    overflow: hidden;
}

.progress-bar::after {
    content: '';
    display: block;
    height: 100%;
    background: linear-gradient(
        90deg,
        #17a2b8,
        #138496
    );
    transition: width 0.3s ease;
}

.progress-text {
    min-width: 50px;
    text-align: right;
    font-weight: 600;
    color: #333;
}

@media (max-width: 768px) {
    .body-content {
        padding: 15px;
    }

    .form-group {
        flex-direction: column;
        gap: 10px;
    }

    .form-item {
        width: 100%;
        min-width: auto;
    }

    .button-group {
        flex-direction: column;
    }

    .excel-button,
    .cancel-button {
        width: 100%;
    }
}
</style>
